import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  ArrowLeft,
  Shield,
  Info,
  UserCheck,
  ShieldCheck,
  Mail,
  FileText,
  ExternalLink,
} from "lucide-react";
import MainLayout from "@/components/layouts/MainLayout";
import { motion } from "framer-motion";

const PrivacyPolicy = () => {
  const lastUpdated = new Date().toLocaleDateString("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <>
      <Helmet>
        <title>Privacy Policy | Numberz Insight</title>
        <meta
          name="description"
          content="Privacy Policy for Numberz Insight - Learn how we collect, use, and protect your personal information."
        />
      </Helmet>

      <MainLayout>
        <div className="relative bg-gradient-to-b from-primary/10 to-background pt-24 pb-16 md:pt-28 md:pb-20 overflow-hidden">
          <div className="absolute inset-0 z-0 opacity-30">
            <img
              src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80"
              alt="Privacy background"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-primary/10 to-background"></div>
          </div>
          <div className="container relative z-10 mx-auto px-4 max-w-5xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Privacy Policy
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                We're committed to protecting your privacy and ensuring your
                data is handled responsibly
              </p>
              <div className="flex items-center justify-center mt-6 text-sm text-muted-foreground">
                <Shield className="h-4 w-4 mr-2" />
                <span>Last updated: {lastUpdated}</span>
              </div>
            </motion.div>
          </div>
        </div>

        <main className="container mx-auto px-4 py-8 max-w-5xl">
          <Tabs defaultValue="overview" className="w-full">
            <div className="sticky top-20 z-10 bg-background/80 backdrop-blur-sm pt-4 pb-2 mb-8">
              <TabsList className="w-full justify-start overflow-x-auto flex p-1 rounded-lg bg-muted/50">
                <TabsTrigger
                  value="overview"
                  className="rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm"
                >
                  <Info className="h-4 w-4 mr-2" />
                  Overview
                </TabsTrigger>
                <TabsTrigger
                  value="collection"
                  className="rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Data Collection
                </TabsTrigger>
                <TabsTrigger
                  value="rights"
                  className="rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm"
                >
                  <UserCheck className="h-4 w-4 mr-2" />
                  Your Rights
                </TabsTrigger>
                <TabsTrigger
                  value="security"
                  className="rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm"
                >
                  <ShieldCheck className="h-4 w-4 mr-2" />
                  Security
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="overview" className="space-y-6">
              <Alert className="bg-primary/5 border-primary/20">
                <Info className="h-4 w-4 text-primary" />
                <AlertTitle>About Our Privacy Policy</AlertTitle>
                <AlertDescription>
                  This policy explains how we collect, use, and protect your
                  personal information.
                </AlertDescription>
              </Alert>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-xl">
                    <Shield className="h-5 w-5 mr-2 text-primary" />
                    Introduction
                  </CardTitle>
                  <CardDescription>
                    Our commitment to your privacy and data protection
                  </CardDescription>
                </CardHeader>
                <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                  <p>
                    Numberz Insight Pty Ltd (ABN: 55 644 962 167) ("we", "our",
                    "us") is committed to protecting your privacy and personal
                    information. This Privacy Policy explains how we collect,
                    use, disclose and protect your personal information, as well
                    as your rights in relation to that information.
                  </p>
                  <p>
                    We are bound by the Australian Privacy Principles (APPs)
                    contained in the Privacy Act 1988 (Cth) ("Privacy Act").
                    This Privacy Policy is designed to comply with relevant
                    privacy legislation and is specifically adapted to the
                    Australian legal context.
                  </p>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Mail className="h-5 w-5 mr-2 text-primary" />
                      Contact Us
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      If you have any questions about our privacy practices,
                      please contact us:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start">
                        <span className="font-medium mr-2">Email:</span>{" "}
                        privacy@numberz.au
                      </li>
                      <li className="flex items-start">
                        <span className="font-medium mr-2">Phone:</span> 1300
                        123 435
                      </li>
                      <li className="flex items-start">
                        <span className="font-medium mr-2">Address:</span> Unit
                        41/2 Fastline Rd, Truganina VIC 3029
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-primary" />
                      Key Points
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start">
                        <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center text-primary mr-2 flex-shrink-0 mt-0.5">
                          1
                        </div>
                        <span>
                          We only collect information necessary to provide our
                          services
                        </span>
                      </li>
                      <li className="flex items-start">
                        <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center text-primary mr-2 flex-shrink-0 mt-0.5">
                          2
                        </div>
                        <span>
                          Your data is secured with industry-standard encryption
                        </span>
                      </li>
                      <li className="flex items-start">
                        <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center text-primary mr-2 flex-shrink-0 mt-0.5">
                          3
                        </div>
                        <span>
                          You can access, correct or delete your personal
                          information
                        </span>
                      </li>
                      <li className="flex items-start">
                        <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center text-primary mr-2 flex-shrink-0 mt-0.5">
                          4
                        </div>
                        <span>We don't sell your data to third parties</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="collection" className="space-y-6">
              <Alert className="bg-primary/5 border-primary/20">
                <FileText className="h-4 w-4 text-primary" />
                <AlertTitle>Data Collection and Usage</AlertTitle>
                <AlertDescription>
                  Information about how we collect, use, and store your data.
                </AlertDescription>
              </Alert>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-xl">
                    <FileText className="h-5 w-5 mr-2 text-primary" />
                    Information We Collect
                  </CardTitle>
                </CardHeader>
                <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                  <p>
                    We collect personal information that is reasonably necessary
                    for our business functions and activities. The types of
                    personal information we may collect include:
                  </p>
                  <ul>
                    <li>
                      <strong>Contact Information:</strong> Name, address, email
                      address, phone number
                    </li>
                    <li>
                      <strong>Identification Information:</strong> Date of
                      birth, tax file number, driver's license details
                    </li>
                    <li>
                      <strong>Financial Information:</strong> Bank account
                      details, income details, tax records
                    </li>
                    <li>
                      <strong>Business Information:</strong> ABN, ACN, business
                      structure, business activities
                    </li>
                    <li>
                      <strong>Employment Information:</strong> Employer details,
                      employment history, salary information
                    </li>
                    <li>
                      <strong>Usage Data:</strong> Information about your
                      interactions with our website and services
                    </li>
                  </ul>

                  <h3>Purpose of Collection</h3>
                  <p>
                    We collect, hold, use and disclose your personal information
                    for the following purposes:
                  </p>
                  <ul>
                    <li>
                      To provide our tax, accounting, and business registration
                      services
                    </li>
                    <li>To verify your identity</li>
                    <li>To communicate with you about our services</li>
                    <li>To comply with our legal and regulatory obligations</li>
                    <li>To process payments</li>
                    <li>To improve our services and develop new ones</li>
                    <li>To conduct business analytics and reporting</li>
                    <li>To protect our legal interests and prevent fraud</li>
                  </ul>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-primary" />
                      Data Retention
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                    <p>
                      We retain your personal information for as long as
                      necessary to fulfill the purposes outlined in this privacy
                      policy, unless a longer retention period is required by
                      law. Tax records are typically retained for 5-7 years in
                      accordance with ATO requirements.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-primary" />
                      Third-Party Sharing
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                    <p>
                      We do not sell your personal information to third parties.
                      We may share your information with:
                    </p>
                    <ul>
                      <li>
                        The Australian Taxation Office and other government
                        agencies as required by law
                      </li>
                      <li>
                        Service providers who assist us in operating our website
                        and services (all bound by confidentiality agreements)
                      </li>
                      <li>
                        Legal and regulatory authorities when required by law
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="rights" className="space-y-6">
              <Alert className="bg-primary/5 border-primary/20">
                <UserCheck className="h-4 w-4 text-primary" />
                <AlertTitle>Your Privacy Rights</AlertTitle>
                <AlertDescription>
                  Understanding your rights regarding your personal information.
                </AlertDescription>
              </Alert>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-xl">
                    <UserCheck className="h-5 w-5 mr-2 text-primary" />
                    Your Rights Under Australian Privacy Law
                  </CardTitle>
                </CardHeader>
                <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                  <p>
                    Under the Australian Privacy Act 1988, you have the right
                    to:
                  </p>
                  <ul>
                    <li>
                      <strong>Access:</strong> Request access to the personal
                      information we hold about you.
                    </li>
                    <li>
                      <strong>Correction:</strong> Request that we correct any
                      inaccurate or incomplete information.
                    </li>
                    <li>
                      <strong>Deletion:</strong> Request deletion of your
                      personal information in certain circumstances.
                    </li>
                    <li>
                      <strong>Objection:</strong> Object to the processing of
                      your personal information in certain circumstances.
                    </li>
                    <li>
                      <strong>Complaint:</strong> Lodge a complaint with the
                      Office of the Australian Information Commissioner (OAIC)
                      if you believe we have violated your privacy rights.
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <UserCheck className="h-5 w-5 mr-2 text-primary" />
                    How to Exercise Your Rights
                  </CardTitle>
                </CardHeader>
                <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                  <p>To exercise any of these rights, please contact us at:</p>
                  <ul>
                    <li>Email: privacy@numberz.au</li>
                    <li>Phone: 1300 123 435</li>
                    <li>Mail: Unit 41/2 Fastline Rd, Truganina VIC 3029</li>
                  </ul>
                  <p>
                    We will respond to your request within 30 days. We may need
                    to verify your identity before processing your request.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="security" className="space-y-6">
              <Alert className="bg-primary/5 border-primary/20">
                <ShieldCheck className="h-4 w-4 text-primary" />
                <AlertTitle>Data Security</AlertTitle>
                <AlertDescription>
                  How we protect your personal and financial information.
                </AlertDescription>
              </Alert>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-xl">
                    <ShieldCheck className="h-5 w-5 mr-2 text-primary" />
                    Security Measures
                  </CardTitle>
                </CardHeader>
                <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                  <p>
                    We implement a variety of security measures to maintain the
                    safety of your personal information:
                  </p>
                  <ul>
                    <li>
                      <strong>Encryption:</strong> All data transmitted between
                      your browser and our servers is encrypted using TLS/SSL
                      technology.
                    </li>
                    <li>
                      <strong>Secure Storage:</strong> Your data is stored in
                      secure cloud environments with industry-standard
                      protections.
                    </li>
                    <li>
                      <strong>Access Controls:</strong> We restrict access to
                      personal information to authorized employees who need it
                      to perform their job functions.
                    </li>
                    <li>
                      <strong>Regular Audits:</strong> We conduct regular
                      security audits and vulnerability assessments.
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <ShieldCheck className="h-5 w-5 mr-2 text-primary" />
                    Data Breach Procedures
                  </CardTitle>
                </CardHeader>
                <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                  <p>In the unlikely event of a data breach, we will:</p>
                  <ul>
                    <li>
                      Promptly notify affected individuals as required by the
                      Notifiable Data Breaches scheme
                    </li>
                    <li>Work to identify and remedy the cause of the breach</li>
                    <li>Cooperate with regulatory authorities</li>
                    <li>Take steps to prevent future breaches</li>
                  </ul>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <div className="mt-16 pt-8 border-t border-border">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-lg font-medium mb-4 flex items-center">
                  <Shield className="h-5 w-5 mr-2 text-primary" />
                  Privacy Commitment
                </h3>
                <p className="text-muted-foreground">
                  We're committed to protecting your privacy and ensuring your
                  data is handled responsibly and securely at all times.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-4 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-primary" />
                  Related Documents
                </h3>
                <ul className="space-y-2">
                  <li>
                    <Link
                      to="/terms"
                      className="text-primary hover:underline flex items-center"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Terms of Service
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/email/privacy-policy"
                      className="text-primary hover:underline flex items-center"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Email Privacy Policy
                    </Link>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-4 flex items-center">
                  <Mail className="h-5 w-5 mr-2 text-primary" />
                  Contact Us
                </h3>
                <p className="text-muted-foreground mb-4">
                  Have questions about our privacy practices?
                </p>
                <Link to="/contact">
                  <Button variant="default">Contact Us</Button>
                </Link>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t border-border text-center">
              <p className="text-sm text-muted-foreground">
                Last updated: {lastUpdated}
              </p>
            </div>
          </div>
        </main>
      </MainLayout>
    </>
  );
};

export default PrivacyPolicy;
